import { BulkApproveDialog } from '@/components/documents/bulk-approve';
import type { DocumentSummary, InboxSummary, Paged } from '@ruby/shared';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Check, Inbox as InboxIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AttachmentsPanel, DocumentHistory, StatusBadge, WorkflowActions } from '@/components/documents/document-parts';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/ui/data-table';
import { Dialog } from '@/components/ui/dialog';
import { Card, EmptyState, PageHeader } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { cn, formatAED, formatDate, formatDateTime } from '@/lib/utils';
import { Brand } from '../shell/brand';
import { UserMenu } from '../shell/user-menu';

export const useInboxSummary = () =>
  useQuery({ queryKey: ['documents', 'inbox', 'summary'], queryFn: () => api<InboxSummary>('/documents/inbox/summary'), refetchInterval: 60_000, staleTime: 30_000 });

/** One queue of everything waiting for the user's approval, across all modules. */
export function InboxPage() {
  const search = useSearch({ strict: false }) as { type?: string; page?: number };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const page = Number(search.page) || 1;
  const type = search.type;
  const summary = useInboxSummary();
  const q = { page, pageSize: 50, docType: type };
  const { data, isFetching } = useQuery({
    queryKey: ['documents', 'inbox', q],
    queryFn: () => api<Paged<DocumentSummary>>('/documents/inbox', { query: q }),
    placeholderData: keepPreviousData,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<DocumentSummary | null>(null);
  const [bulk, setBulk] = useState<DocumentSummary[] | null>(null);

  // Accounting vouchers are approved only in Accounting › Approvals › Pending Approval; they open there.
  const isVoucher = (d: DocumentSummary) => d.docType.startsWith('acct.');
  const canTick = (d: DocumentSummary) => d.actions.includes('approve') && !isVoucher(d);
  const approvable = (data?.items ?? []).filter(canTick);
  const setFilter = (t?: string) => void navigate({ to: '/inbox', search: { type: t } as never, replace: true });

  const columns = useMemo<ColumnDef<DocumentSummary, unknown>[]>(
    () => [
      {
        id: 'sel',
        size: 40,
        header: () => (
          <Checkbox
            aria-label="Select all"
            checked={approvable.length > 0 && approvable.every((d) => selected.has(d.id))}
            onCheckedChange={(c) => setSelected(c ? new Set(approvable.map((d) => d.id)) : new Set())}
          />
        ),
        cell: ({ row }) =>
          canTick(row.original) ? (
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox
                aria-label={`Select ${row.original.number}`}
                checked={selected.has(row.original.id)}
                onCheckedChange={(c) => setSelected((s) => { const n = new Set(s); if (c) n.add(row.original.id); else n.delete(row.original.id); return n; })}
              />
            </span>
          ) : null,
      },
      { header: 'Number', cell: ({ row }) => <span className="font-medium">{row.original.number}</span> },
      { header: 'Type', cell: ({ row }) => row.original.docTypeLabel },
      { header: 'Date', cell: ({ row }) => <span className="tabular">{formatDate(row.original.docDate)}</span> },
      { header: 'Party / title', cell: ({ row }) => <span className="block max-w-xs truncate">{row.original.partyName ?? row.original.title ?? '—'}</span> },
      { header: 'Amount', cell: ({ row }) => <span className="tabular">{row.original.amount === null ? '—' : formatAED(row.original.amount)}</span> },
      { header: 'Created by', cell: ({ row }) => row.original.createdBy?.name ?? '—' },
      {
        header: 'Submitted',
        cell: ({ row: { original: d } }) => (
          <div className="leading-tight">
            <span className="text-fg-muted">{formatDateTime(d.submittedAt)}</span>
            {d.overdue && <span className="fx-risk mt-0.5 block w-fit rounded-full bg-danger-soft px-2 text-[11px] font-semibold text-danger">Waiting {d.waitingHours}h</span>}
            {d.approval?.current && <span className="block text-[11px] text-fg-subtle">Step: {d.approval.current} ({d.approval.levels.filter((l) => l.done).length + 1}/{d.approval.levels.length})</span>}
          </div>
        ),
      },
    ],
    [approvable, selected],
  );

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex h-[68px] max-w-[1440px] items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 text-[13px] font-medium text-fg-muted hover:text-primary">
              <ArrowLeft className="size-4" /> Modules
            </Link>
            <Brand subtitle="Approval Inbox" />
          </div>
          <UserMenu />
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6">
        <PageHeader
          title="Approval Inbox"
          description="Everything waiting for your approval, across all modules."
          actions={
            selected.size > 0 && (
              <Button onClick={() => setBulk(approvable.filter((d) => selected.has(d.id)))}>
                <Check /> Approve {selected.size} selected
              </Button>
            )
          }
        />
        <div className="mb-4 flex flex-wrap gap-2">
          <FilterChip active={!type} onClick={() => setFilter(undefined)} label="All" count={summary.data?.total ?? 0} />
          {summary.data?.byType.map((t) => <FilterChip key={t.docType} active={type === t.docType} onClick={() => setFilter(t.docType)} label={t.label} count={t.count} />)}
        </div>
        {data && data.total === 0 && !type ? (
          <Card>
            <EmptyState icon={<InboxIcon />} title="Nothing waiting for you" description="Documents submitted for your approval will appear here." />
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={data?.items}
            total={data?.total ?? 0}
            page={page}
            pageSize={50}
            loading={isFetching}
            onPageChange={(p) => void navigate({ to: '/inbox', search: { type, page: p } as never })}
            onRowClick={(d) => (isVoucher(d) ? void navigate({ to: '/m/$module/$screen/$id', params: { module: 'accounting', screen: 'voucher_manager', id: d.id }, state: { fromList: true } as never }) : setOpen(d))}
          />
        )}
        <BulkApproveDialog items={bulk?.map((d) => ({ id: d.id, version: d.version, number: d.number, label: `${d.docTypeLabel} · ${d.partyName || d.title}`, amount: d.amount, by: d.createdBy?.name })) ?? null} onClose={() => setBulk(null)} onDone={() => { setSelected(new Set()); void qc.invalidateQueries({ queryKey: ['documents'] }); }} />
      </main>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)} title={open ? `${open.docTypeLabel} ${open.number ?? ''}` : ''} description={open ? `${formatDate(open.docDate)} · ${open.createdBy?.name ?? ''}` : ''} size="lg">
        {open && <DocumentQuickView doc={open} onChanged={(d) => (d.status === 'PENDING' ? setOpen(d) : setOpen(null))} />}
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }: { active: boolean; onClick(): void; label: string; count: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors', active ? 'border-primary bg-primary text-white' : 'border-border bg-surface hover:border-accent')}
    >
      {label}
      <span className={cn('tabular rounded-full px-1.5 text-[11px]', active ? 'bg-white/20' : 'bg-primary-soft text-primary')}>{count}</span>
    </button>
  );
}

/** Compact view of a document for approval: header facts, actions, history and attachments. */
export function DocumentQuickView({ doc, onChanged }: { doc: DocumentSummary; onChanged(d: DocumentSummary): void }) {
  return (
    <div className="grid gap-5 md:grid-cols-[1fr_260px]">
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
          <dt className="text-fg-subtle">Status</dt>
          <dd><StatusBadge status={doc.status} /></dd>
          <dt className="text-fg-subtle">Party / title</dt>
          <dd>{doc.partyName ?? doc.title ?? '—'}</dd>
          <dt className="text-fg-subtle">Amount</dt>
          <dd className="tabular font-semibold">{doc.amount === null ? '—' : formatAED(doc.amount)}</dd>
          {doc.legacyNumber && (<><dt className="text-fg-subtle">Old system no.</dt><dd>{doc.legacyNumber}</dd></>)}
        </dl>
        <WorkflowActions doc={doc} onDone={onChanged} />
        <div>
          <p className="mb-2 text-[13px] font-semibold">Attachments</p>
          <AttachmentsPanel documentId={doc.id} canUpload={false} canRemove={false} />
        </div>
      </div>
      <div>
        <p className="mb-3 text-[13px] font-semibold">History</p>
        <DocumentHistory documentId={doc.id} />
      </div>
    </div>
  );
}
